<script lang="ts">import { FeatureSet, ReleaseStage, stateFeatureSet, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isMobile, loggedIn } from "@infinitro/global";
import { ScrollUpIcon } from "@infinitro/icons";
import { LeaderboardCard } from "@infinitro/races";
import { onDestroy, onMount, tick } from "svelte";
let hideScrollMsgPermanently = false;
let leaderboardEl = null;
let scrollMsgEl = null;
let io = null;
let seenLeaderboard = false;
let leaderboardInView = false;
let hideDesktopLeaderboard = false;
let scrollRootEl = null;
let scrollBottomEl = null;
let assistNextDownScroll = false;
let lastY = 0;
let onScroll = null;
let scrollListenerTarget = null;
const HEADER_OFFSET = 0;
function getScrollParent(node) {
	let p = node?.parentElement || null;
	while (p) {
		const style = getComputedStyle(p);
		const overflowY = style.overflowY;
		if ((overflowY === "auto" || overflowY === "scroll") && p.scrollHeight > p.clientHeight) {
			return p;
		}
		p = p.parentElement;
	}
	return document.scrollingElement;
}
function scrollToMsg(force = false) {
	if (!scrollMsgEl) return;
	const parent = getScrollParent(scrollMsgEl);
	if (!parent) return;
	const rect = scrollMsgEl.getBoundingClientRect();
	const parentRect = parent.getBoundingClientRect();
	const currentTopInParent = rect.top - parentRect.top + parent.scrollTop;
	const nearTop = Math.abs(rect.top - (parentRect.top + HEADER_OFFSET)) < 4;
	if (!nearTop || force) {
		parent.scrollTo({
			top: currentTopInParent - HEADER_OFFSET,
			left: 0,
			behavior: "auto"
		});
	}
}
const getY = () => {
	if (scrollRootEl && scrollRootEl !== document.scrollingElement) {
		return scrollRootEl.scrollTop;
	}
	return window.pageYOffset ?? window.scrollY ?? document.documentElement.scrollTop ?? 0;
};
onMount(async () => {
	if ("scrollRestoration" in history) history.scrollRestoration = "manual";
	await tick();
	let tries = 0;
	const maxTries = 6;
	const kick = () => {
		scrollToMsg(true);
		if (++tries < maxTries) requestAnimationFrame(kick);
	};
	requestAnimationFrame(kick);
	const onLoad = () => scrollToMsg(true);
	window.addEventListener("load", onLoad);
	window.addEventListener("resize", onLoad);
	setTimeout(() => {
		if (leaderboardEl) {
			scrollRootEl = getScrollParent(leaderboardEl);
			io = new IntersectionObserver((entries) => {
				const [entry] = entries;
				// Oculta el mensaje solo la primera vez que se ve
				if (entry.isIntersecting && !seenLeaderboard) {
					hideScrollMsgPermanently = true;
					seenLeaderboard = true;
				}
				// Re-armar asistencia CADA VEZ que el leaderboard vuelve a entrar
				if (entry.isIntersecting && !leaderboardInView) {
					leaderboardInView = true;
					assistNextDownScroll = true;
					lastY = getY();
				} else if (!entry.isIntersecting && leaderboardInView) {
					leaderboardInView = false;
				}
			}, {
				root: scrollRootEl === document.scrollingElement ? null : scrollRootEl,
				threshold: .1,
				rootMargin: "0px"
			});
			io.observe(leaderboardEl);
			// usar window cuando el root real es el documento
			scrollListenerTarget = scrollRootEl && scrollRootEl !== document.scrollingElement ? scrollRootEl : window;
			onScroll = () => {
				const currentY = getY();
				if (assistNextDownScroll && currentY > lastY + 10) {
					assistNextDownScroll = false;
					scrollBottomEl?.scrollIntoView({
						behavior: "smooth",
						block: "start"
					});
				}
				lastY = currentY;
			};
			scrollListenerTarget.addEventListener("scroll", onScroll);
		}
	}, 100);
	return () => {
		window.removeEventListener("load", onLoad);
		window.removeEventListener("resize", onLoad);
	};
});
onDestroy(() => {
	io?.disconnect();
	if (onScroll && scrollListenerTarget) {
		scrollListenerTarget.removeEventListener("scroll", onScroll);
	}
});
$: canShowRaces = $loggedIn === true && $stateUi.games.showRacesLeaderboard && $stateFeatureSet === FeatureSet.Preview && wantAtLeastReleaseStage(ReleaseStage.Alpha);
$: showDesktopLeaderboard = !$isMobile && canShowRaces && !hideDesktopLeaderboard;
</script>

{#if showDesktopLeaderboard}
	<div class="leaderboard-block">
		<div class="race-leaderboard-container" bind:this={leaderboardEl}>
			<LeaderboardCard maxLength={3} raceId="current" />
		</div>

		<div
			class="scroll-message-container"
			class:is-permanently-hidden={hideScrollMsgPermanently}
			bind:this={scrollMsgEl}
		>
			<div class:is-hidden={hideScrollMsgPermanently}>
				<ScrollUpIcon />
			</div>
			<p class="scroll-message" class:is-hidden={hideScrollMsgPermanently}>
				{$t(Verbiage.scroll_to_see_leaderboards)}
			</p>
			<div class:is-hidden={hideScrollMsgPermanently}>
				<ScrollUpIcon />
			</div>
		</div>
		<div bind:this={scrollBottomEl} class="scroll-bottom-marker"></div>
	</div>
{/if}

<style>
	.leaderboard-block {
		margin-bottom: 0.5rem;
		width: 100%;
}

	.race-leaderboard-container {
		margin-top: 0.5rem;
		margin-bottom: 0.5rem;
		width: 100%;
		max-width: calc((var(--app-vh, 100vh) - 72px - 50px) * 16 / 9);
		max-width: calc((100dvh - 72px - 50px) * 16 / 9);
		margin-left: auto;
		margin-right: auto;
}
	.scroll-message-container {
		display: flex;
		height: 40px;
		width: 100%;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		opacity: 0.6;
		max-width: calc((var(--app-vh, 100vh) - 72px - 50px) * 16 / 9);
		max-width: calc((100dvh - 72px - 50px) * 16 / 9);
		margin-left: auto;
		margin-right: auto;
		transition: opacity 180ms ease;
}
	.scroll-message-container.is-permanently-hidden {
		height: 0px;
		overflow: hidden;
}
	.scroll-message {
		font-size: 14px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	.is-hidden {
		opacity: 0;
	}
</style>
